import { combineReducers } from "redux";
import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import type {
    ParsedContactsResult,
    ParseDomainsResult,
    ParsingErrors,
} from "app/pages/sales-intelligence/sub-modules/management/components/types";

import * as actions from "app/pages/sales-intelligence/modules/enrichment/store/actions/enrichment";
import type { EnrichmentConfigItem } from "app/pages/sales-intelligence/modules/enrichment/store/type";

export type EnrichmentState = {
    selectedColumnsConfig: {
        company: {
            config: readonly EnrichmentConfigItem[];
            isLoading: boolean;
        };
        contact: {
            config: readonly EnrichmentConfigItem[];
            isLoading: boolean;
        };
    };
    preview: {
        totalRows: number;
        matchedRows: number;
        isLoading: boolean;
    };
    parsing: {
        isParsing: boolean;
        parsingResult: ParseDomainsResult;
        contactsParsingResult: ParsedContactsResult;
        error: ParsingErrors;
    };
    isEnriching: boolean;
    hasEnrichmentLoadingError: boolean;
};

const INITIAL_ENRICHMENT_STATE: EnrichmentState = {
    selectedColumnsConfig: {
        company: {
            config: [],
            isLoading: false,
        },
        contact: {
            config: [],
            isLoading: false,
        },
    },
    preview: {
        totalRows: null!,
        matchedRows: null!,
        isLoading: false,
    },
    parsing: {
        isParsing: false,
        parsingResult: {
            allDomainValues: {},
            domainColumn: "",
            headerNames: [],
            parsedDomains: [],
            totalRowCount: null!,
        },
        contactsParsingResult: {
            fileParsed: {},
            linkedinHeaderNames: [],
            fullnameHeaderNames: [],
            companyHeaderNames: [],
            headerNames: [],
            totalRowCount: null!,
        },
        error: { columnsNumber: false, format: false, isOverTheLimit: false, notADomains: false },
    },
    isEnriching: false,
    hasEnrichmentLoadingError: false,
};

const enrichmentReducer = createReducer<EnrichmentState, ActionType<typeof actions>>(INITIAL_ENRICHMENT_STATE)
    .handleAction(actions.getPreviewAsync.request, (state) => ({
        ...state,
        preview: {
            ...state.preview,
            isLoading: true,
        },
    }))
    .handleAction(actions.getPreviewAsync.success, (state, { payload }) => ({
        ...state,
        preview: {
            ...state.preview,
            totalRows: payload.totalRows,
            matchedRows: payload.matchedRows,
            isLoading: false,
        },
    }))
    .handleAction(actions.getPreviewAsync.failure, (state) => ({
        ...state,
        preview: {
            ...state.preview,
            isLoading: false,
        },
    }))
    .handleAction(actions.getSelectedColumnsConfigAsync.request, (state) => ({
        ...state,
        selectedColumnsConfig: {
            ...state.selectedColumnsConfig,
            company: { ...state.selectedColumnsConfig.company, isLoading: true },
        },
    }))
    .handleAction(actions.getSelectedColumnsConfigAsync.success, (state, { payload }) => ({
        ...state,
        selectedColumnsConfig: {
            ...state.selectedColumnsConfig,
            company: {
                config: payload,
                isLoading: false,
            },
        },
    }))
    .handleAction(actions.getSelectedColumnsConfigAsync.failure, (state) => ({
        ...state,
        selectedColumnsConfig: {
            ...state.selectedColumnsConfig,
            company: {
                ...state.selectedColumnsConfig.company,
                isLoading: false,
            },
        },
    }))
    .handleAction(actions.getContactsSelectedColumnsConfigAsync.request, (state) => ({
        ...state,
        selectedColumnsConfig: {
            ...state.selectedColumnsConfig,
            contact: {
                ...state.selectedColumnsConfig.contact,
                isLoading: true,
            },
        },
    }))
    .handleAction(actions.getContactsSelectedColumnsConfigAsync.success, (state, { payload }) => ({
        ...state,
        selectedColumnsConfig: {
            ...state.selectedColumnsConfig,
            contact: {
                config: payload,
                isLoading: false,
            },
        },
    }))
    .handleAction(actions.getContactsSelectedColumnsConfigAsync.failure, (state) => ({
        ...state,
        selectedColumnsConfig: {
            ...state.selectedColumnsConfig,
            contact: {
                ...state.selectedColumnsConfig.contact,
                isLoading: false,
            },
        },
    }))
    .handleAction(actions.parseEnrichmentCsvAsync.request, (state) => ({
        ...state,
        parsing: {
            ...state.parsing,
            isParsing: true,
        },
    }))
    .handleAction(actions.parseEnrichmentCsvAsync.success, (state, { payload }) => ({
        ...state,
        parsing: {
            ...state.parsing,
            parsingResult: payload,
            isParsing: false,
            error: INITIAL_ENRICHMENT_STATE.parsing.error,
        },
    }))
    .handleAction(actions.parseEnrichmentCsvAsync.failure, (state, { payload }) => ({
        ...state,
        parsing: {
            ...state.parsing,
            isParsing: false,
            error: payload,
        },
    }))
    .handleAction(actions.parseContactsEnrichmentCsvAsync.request, (state) => ({
        ...state,
        parsing: {
            ...state.parsing,
            isParsing: true,
        },
    }))
    .handleAction(actions.parseContactsEnrichmentCsvAsync.success, (state, { payload }) => ({
        ...state,
        parsing: {
            ...state.parsing,
            contactsParsingResult: payload,
            isParsing: false,
            error: INITIAL_ENRICHMENT_STATE.parsing.error,
        },
    }))
    .handleAction(actions.parseContactsEnrichmentCsvAsync.failure, (state, { payload }) => ({
        ...state,
        parsing: {
            ...state.parsing,
            isParsing: false,
            error: payload,
        },
    }))
    .handleAction(actions.postEnrichmentCsvAsync.request, (state) => ({
        ...state,
        isEnriching: true,
    }))
    .handleAction(actions.postEnrichmentCsvAsync.success, (state) => ({
        ...state,
        isEnriching: false,
        hasEnrichmentLoadingError: false,
    }))
    .handleAction(actions.postEnrichmentCsvAsync.failure, (state) => ({
        ...state,
        isEnriching: false,
        hasEnrichmentLoadingError: true,
    }))
    .handleAction(actions.postContactsEnrichmentCsvAsync.request, (state) => ({
        ...state,
        isEnriching: true,
    }))
    .handleAction(actions.postContactsEnrichmentCsvAsync.success, (state) => ({
        ...state,
        hasEnrichmentLoadingError: false,
    }))
    .handleAction(actions.postContactsEnrichmentCsvAsync.failure, (state) => ({
        ...state,
        isEnriching: false,
        hasEnrichmentLoadingError: true,
    }))
    .handleAction(actions.enrichmentFinishedAction, (state) => ({
        ...state,
        isEnriching: false,
        hasEnrichmentLoadingError: false,
    }))
    .handleAction(actions.clearEnrichmentParsing, (state) => ({
        ...state,
        parsing: INITIAL_ENRICHMENT_STATE.parsing,
    }));

export const myListsReducer = combineReducers({ enrichment: enrichmentReducer });
